import { createSlice } from '@reduxjs/toolkit';

import produce from 'immer';
import {
  feedListAt,
  isFeedListValue,
  isMultiFeedFirmwarePayload,
  parseFeedList,
  parseFeedTotalList,
} from '@/functions/feedList';
import {
  isMultiMixerFirmwarePayload,
  mixerListAt,
  parseMixerList,
} from '@/functions/mixerList';

/** Maps raw fast payload indices to GOZ-shaped fields (GPI / GPC installations). */
function gozFastsFromPayload(data) {
  // f101 FEED_TOTAL = [liquid1, liquid2, liquid3] → stored as [null, l1, l2, l3]
  const feedingTotalizerList = parseFeedTotalList(data[101]);
  const multiFeedFromFast = isFeedListValue(data[101]);
  return {
    multiFeedFirmware: multiFeedFromFast,
    gasBufferLevel: data[5],
    blowerPressure: data[66],
    reactorTemp: data[1],
    h2sAfterFilter: data[21],
    ch4BiogasSupply: data[18],
    co2BiogasSupply: data[19],
    o2BiogasSupply: data[20],
    oilCoolerTemp: data[12],
    valveMembrane: data[54],
    ch4Biomethane: data[23],
    co2Biomethane: data[24],
    o2Biomethane: data[25],
    valveGrid: data[55],
    purificationState: data[57],
    installationState: data[14],
    compressorState: data[53],
    compressorSpeed: data[71],
    p1PressureOutletBlower: data[66],
    p2PressureOutletBlowerSetpoint: data[67],
    pressureDropInletFilter: data[68],
    p3PressureOutletCompressor: data[72],
    pressureDropOilFilter: data[74],
    pressureDropFilterTrain: data[69],
    p4PressureInletMembranes: data[42],
    p5PressureMembraneStage3: data[64],
    p6PressureOutletMembranes: data[65],
    t1TemperatureAfterGascooler: data[11],
    t2TemperatureInletCompressor: data[70],
    t3TemperatureOutletCompressor: data[73],
    t4TemperatureAfterCooling: data[75],
    t5TemperatureAfterReheating: data[43],
    biomethaneFlow: data[22],
    biomethaneDewpoint: data[63],
    wobbe: data[38],
    h2sBiomethane: data[26],
    wobbeHourAverage: data[39],
    injectedGas: data[41],

    hydrostaticPressure: data[4],
    waterTempIn: data[2],
    waterTempOut: data[3],
    waterPressure: data[52],
    mixer: data[30],
    statusValvePumpOut: data[27],
    valveCurrentActuator: data[7],
    feedingStatus: data[32],
    feedingError: data[46],
    intakePumpTime: data[36],
    intakePumpAmount: data[37],
    exitPumpTime: data[34],
    exitPumpAmount: data[35],
    powerConsumption: data[13],

    emergencyStop: data[80],
    maintenance: data[81],
    ssrOutput: data[83],
    /** DI open/closed string from firmware (open = 1, closed = 0); analogous to ssrOutput. */
    diStatus: data[93],
    valve3: data[27],

    energyMeterPurification: data[84],
    energyMeter: data[17],
    gozError: data[82],
    torchStatus: data[61],

    tBrineOut: data[88],
    timeOutHeatPumpGasCooler: data[87],
    compressorHeatPumpSpeed: data[89],
    heatPumpCondenserInTemperature: data[108],
    heatPumpCondenserOutTemperature: data[109],
    powerConsumptionPurification: data[85],
    powerConsumptionHeatPump: data[92],
    energyMeterDigesterTank: data[90],

    exitPumpRunning: data[28],
    // Same indices: OT thermal on older GPI/GPC; navergister on multi-feed GPI.
    // UI chooses via multiFeedFirmware flag.
    foamBeaterError: data[48],
    otExitPump: data[49],
    navergisterTReactor: data[47],
    navergisterTIn: data[48],
    navergisterTOut: data[49],
    gasbufPost: data[15],
    reactorLvl2: data[16],
    feedingTotalizerList,
    feedingTotalizer: parseFeedTotalList(data[101])[1],

    hopperFeedingWeight: data[104],
    biomethaneFlowMaster: data[99],
    torchTotalRunningMinutes: data[95],
    lowPressureGrid: data[94],
    thtDensity: data[97],
    panasonicHeatPumpStatus: data[62],
    panasonicWindowNr: data[111],
    panasonicControlStatus: data[112],
  };
}

/** Maps raw slow payload indices to GOZ-shaped fields. */
function gozSlowsFromPayload(data) {
  const gpType = Number(data[97]);
  const isGpi = gpType === 1;
  const multiFeedFirmware = isGpi && isMultiFeedFirmwarePayload(data);
  const multiMixerFirmware = isGpi && isMultiMixerFirmwarePayload(data);
  // Dual reactor offset: s84 = [main, post]; scalar legacy = main only
  const dualReactorOffset = isGpi && isFeedListValue(data[84]);

  const feedingVolumeList = parseFeedList(data[12]);
  const feedingCyclesPerDayList = parseFeedList(data[47]);
  const autoFeedingStartHourList = parseFeedList(data[30]);
  const feedingBalanceList = parseFeedList(data[35]);
  const startHourLastFeedingList = parseFeedList(data[73]);
  const mixEveryXMinutesList = parseMixerList(data[41]);
  const mixForXSecondsList = parseMixerList(data[42]);
  const reactorOffsetList = parseMixerList(data[84]);

  return {
    type: data[97],
    multiFeedFirmware,
    multiMixerFirmware,
    dualReactorOffset,
    minGasBufferLevel: data[34],
    maxGasBufferLevel: data[33],
    h2sLevelReactor: data[4],
    filterSelect: data[7],
    h2SBetweenFilter1: data[37],
    h2SBetweenFilter2: data[38],

    reactorTempTarget: data[13],
    reactorLevel: data[14],
    siloType: data[59],
    frostIndex: data[17],
    mixerMaxPower: data[82],
    autoFeedingActive: data[16],
    feedingVolumeList,
    feedingVolume: feedListAt(data[12], 1),
    feedingCyclesPerDayList,
    feedingCyclesPerDay: feedListAt(data[47], 1),
    autoFeedingStartHourList,
    autoFeedingStartHour: feedListAt(data[30], 1),
    feedingBalanceList,
    feedingBalance: feedListAt(data[35], 1),
    intakeTimeout: data[28],
    pumpOutFlowRate: data[15],
    kiloPerPulse: data[25],
    startHourLastFeedingList,
    // Legacy scalar timestamp lives in slow[73]; list firmware uses slot for FEED_SELECT
    startHourLastFeeding: multiFeedFirmware
      ? feedListAt(data[73], Number(data[64]) >= 0 && Number(data[64]) <= 3 ? Number(data[64]) : 1)
      : data[73],
    airConcentratorMinutesPerHour: data[23],

    airInjectionTime: data[22],
    maxAirInjectionTime: data[74],
    torchType: data[21],
    oilInjectionsForXSec: data[61],
    oilInjectionsEveryXMin: data[60],
    filterLoad: data[39],
    filterLoad2: data[40],

    hostname: data[3],
    installationIdentifier: data[1],
    rebootMainPrint: data[53],
    rebootSom: data[98],
    utcOffset: data[83],
    ip: data[8],
    mask: data[9],
    gate: data[10],
    dns: data[11],

    firmwareVersion: data[2],
    printId: data[70],
    h2sSensorFirmwareVersion: data[56],
    idH2s: data[88],

    gasBufferLevelSetting: data[32],

    // Dual-mixer firmware: s41/s42 = [mixer1, mixer2]; scalar fields stay mixer1 for charts/stats
    mixEveryXMinutesList,
    mixEveryXMinutes: multiMixerFirmware ? mixerListAt(data[41], 1) : data[41],
    mixForXSecondsList,
    mixForXSeconds: multiMixerFirmware ? mixerListAt(data[42], 1) : data[42],
    blockMixingFrom: data[43],
    blockMixingTo: data[44],
    blockMixingFrom2: data[45],
    blockMixingTo2: data[46],
    mixDuringIntake: data[101],
    mixTimeBeforeAfterDryIntake: data[104],

    extraHeating: data[18],
    ssr: data[72],
    hardwareVersion: data[26],
    mixerDelay: data[19],
    reactorOffsetList,
    reactorOffset: dualReactorOffset ? mixerListAt(data[84], 1) : data[84],
    reactorOffsetPost: dualReactorOffset ? mixerListAt(data[84], 2) : null,

    gpType: data[97],
    allowedFlowToMaster: data[99],

    compressorCurrentRunningMinutes: data[20],
    compressorTotalRunningMinutes: data[24],
    compressorRemainingRunningMinutes: data[31],
    // New multi-feed GPI: s64/s65 = FEED_SELECT / NO_FEEDS
    // Older GPI / GPC: s64/s65 = compressor motor minutes
    ...(multiFeedFirmware
      ? {
          feedSelect: data[64],
          noFeeds: data[65],
          // s66 REACTOR_TYPE_POST (navergister silotype); on older FW s66 was foam beater flag
          siloTypePost: data[66],
        }
      : {
          feedSelect: null,
          noFeeds: 1,
          siloTypePost: null,
          compressorMotorCurrentRunningMinutes: data[64],
          compressorMotorRemainingRunningMinutes: data[65],
        }),
    hopperMassPerCycle: data[102],
    mixDuringSolidFeedIntake: data[103],
    hopperActive: data[105],
    
    actuatorSetting: data[100],
    blockHighElectricalConsumption: data[106],
    thtPidSettings: data[112],

    installedHeatPumps: data[108],
    installedHeatPumpsPostDigester: data[109],
    heatPumpTempPrimaryDigester: data[110],
    heatPumpTempSecondaryDigester: data[111],
    heatPumpActiveWindows: data[113],

    oxygenConcentratorInstalled: data[107],
  };
}

const initialState = {
  wkk: {},
  goz: {},
  gozSlave: {},
};

const machineSlice = createSlice({
  name: 'machine',
  initialState,
  reducers: {
    setWkkFasts(state, action) {
      const data = action.payload;

      return produce(state, draft => {
        draft.wkk = {
          ...draft.wkk,
          power: data[11],
          power2: data[12],
          throttleValve: data[18],
          throttleValve2: data[19],
          rpm: data[20],
          rpm2: data[21],
          lambda: data[38],
          lambda2: data[39],
          engineStatus: data[22],
          engineStatus2: data[23],
          engineError: data[24],
          engineError2: data[25],
          producedEnergy: data[15],
          producedEnergy2: data[16],
          gasUsage: data[48],
          gasUsage2: data[49],
          waterTempMotorOut: data[59],
          waterTempMotorOut2: data[60],
          throttlePosition: data[40],
          throttlePosition2: data[41],
          exitPumpRunning: data[28],
          feedingStatus: data[32],
          feedingError: data[46],
          feedingCycleLevel: data[33],
          exitPumpTime: data[34],
          exitPumpAmount: data[35],
          intakePumpTime: data[36],
          intakePumpAmount: data[37],
          statusValvePumpIn: data[26],
          statusValvePumpOut: data[27],
          statusValveRetourReactor: data[47],
          valveCurrentActuator: data[7],
          gasBufferLevel: data[5],
          voltagePerPhase1: data[8],
          voltagePerPhase2: data[9],
          voltagePerPhase3: data[10],
          frequencyPerPhase: data[44],
          angleBetweenPhase1And2: data[45],
          powerConsumption: data[13],
          consumedEnergy: data[17],
          reactorTemp: data[1],
          waterTempIn: data[2],
          waterTempOut: data[3],
          coolingWaterValvePosition: data[54],
          coolingWaterValvePosition2: data[55],
          hydrostaticPressure: data[4],
          mixer: data[30],
          batteryVoltage: data[14],
          controlCardTemperature: data[6],
          waterPressure: data[52],
          // statusCoolingValve: data[31],
          torchStatus: data[61],
          servoAutoManual: data[62],
          servoAutoManual2: data[63],
          engineTemp: data[42],
          engineTemp2: data[43],
          valve2: data[26],
          valve3: data[27],
          valve4: data[47],
          waterTempAfterHeatExchanger: data[58],
          controlUnitProgress: data[53],
          powerExtern: data[64],
          producedEnergyExtern: data[65],
          // Feeding totalizer (newer WKK firmware); older firmware leaves this unused/xx
          feedingTotalizer: data[66],
          intakePump: data[29],
        };
      });
    },
    setWkkSlows(state, action) {
      const data = action.payload;
      return produce(state, draft => {
        draft.wkk = {
          ...draft.wkk,
          maxPower: data[78],
          maxPower2: data[79],
          desiredPower: data[23],
          desiredPower2: data[24],
          operatingHours: data[6],
          operatingHours2: data[7],
          totalOperatingHours: data[48],
          totalOperatingHours2: data[49],
          hoursUntilService: data[31],
          hoursUntilService2: data[32],
          engineType: data[80],
          engineType2: data[81],
          idBio: data[86],
          idBio2: data[87],
          idH2s: data[88],
          idH2s2: data[89],
          bioIdCode: data[90],
          bioId2Code: data[91],
          h2sLevelReactor: data[4],
          h2sLevelReactor2: data[51],
          h2sLevelMotor: data[37],
          h2sLevelMotor2: data[38],
          filterLoad: data[39],
          filterLoad2: data[40],
          h2sSensorFirmwareVersion: data[56],
          h2sSensorFirmwareVersion2: data[57],
          biotronicFirmwareVersion: data[54],
          biotronicFirmwareVersion2: data[55],
          bioguardVersion: data[77],

          siloType: data[59],
          feedingVolume: data[12],
          feedingCyclesPerDay: data[47],
          reactorLevel: data[14],
          autoFeedingStartHour: data[30],
          autoFeedingActive: data[16],
          intakeTimeout: data[28],
          feedingBalance: data[35],
          pumpOutFlowRate: data[15],
          kiloPerPulse: data[25],
          startHourLastFeeding: data[73],

          minGasBufferLevel: data[34],
          maxGasBufferLevel: data[33],
          defaultMinGasBufferLevel: data[95],
          defaultMaxGasBufferLevel: data[94],
          airInjectionTime: data[22],
          maxAirInjectionTime: data[74],
          oilInjectionsEveryXMin: data[60],
          oilInjectionsForXSec: data[61],
          torchType: data[21],
          frostIndex: data[17],

          reactorTempTarget: data[13],
          coolingWaterTemp: data[50],
          antiFreezeIndex: data[17],
          extraHeating: data[18],
          mixEveryXMinutes: data[41],
          mixForXSeconds: data[42],
          blockMixingFrom: data[43],
          blockMixingTo: data[44],
          blockMixingFrom2: data[45],
          blockMixingTo2: data[46],

          installationIdentifier: data[1],
          firmwareVersion: data[2],
          printId: data[70],
          cooperationContract: data[27],
          ip: data[8],
          mask: data[9],
          gate: data[10],
          dns: data[11],
          slowTensionError: data[19],
          overTemperatureFlueGasHeatExchanger: data[20],
          rebootMainPrint: data[53],
          mixerMaxPower: data[82],
          utcOffset: data[83],
          amountOfEngines: data[5],
          upsEnabled: data[76],
          easyStart: data[64],
          easyStart2: data[65],
          foamBeaterInstalled: data[66],
          oilInjectionsInstalled: data[69],
          coolwaterValveInstalled: data[75],
          monitoringsRelais: data[71],

          hostname: data[3],
          permanence: data[96],
          ssr: data[72],
          language: data[29],

          lambdaUpperLimit: data[67],
          lambdaLowerLimit: data[68],

          bioId: data[62],
          bioId2: data[63],

          reactorOffset: data[84],
          hardwareVersion: data[26],

          mixerSettings: data[98],
          // enableNcrfg: data[84],
          // droopSetting: data[67],
          // frequencyThreshold: data[68],
          // offDelay: data[90],

          actuatorSetting: data[100],

          airIndexManual: data[99],
          minLimitH2SAeration: data[100],
          maxLimitH2SAeration: data[101],
        };
      });
    },
    setGozFasts(state, action) {
      const data = action.payload;
      return produce(state, draft => {
        const next = gozFastsFromPayload(data);
        draft.goz = {
          ...draft.goz,
          ...next,
          // Keep true once detected from slows or fasts
          multiFeedFirmware: Boolean(draft.goz.multiFeedFirmware || next.multiFeedFirmware),
        };
      });
    },
    setGozSlaveFasts(state, action) {
      const data = action.payload;
      return produce(state, draft => {
        const next = gozFastsFromPayload(data);
        draft.gozSlave = {
          ...draft.gozSlave,
          ...next,
          multiFeedFirmware: Boolean(draft.gozSlave.multiFeedFirmware || next.multiFeedFirmware),
        };
      });
    },
    setGozSlows(state, action) {
      const data = action.payload;
      return produce(state, draft => {
        const next = gozSlowsFromPayload(data);
        draft.goz = {
          ...draft.goz,
          ...next,
          multiFeedFirmware: Boolean(draft.goz.multiFeedFirmware || next.multiFeedFirmware),
          multiMixerFirmware: Boolean(draft.goz.multiMixerFirmware || next.multiMixerFirmware),
          dualReactorOffset: Boolean(draft.goz.dualReactorOffset || next.dualReactorOffset),
        };
      });
    },
    setGozSlaveSlows(state, action) {
      const data = action.payload;
      return produce(state, draft => {
        const next = gozSlowsFromPayload(data);
        draft.gozSlave = {
          ...draft.gozSlave,
          ...next,
          multiFeedFirmware: Boolean(draft.gozSlave.multiFeedFirmware || next.multiFeedFirmware),
          multiMixerFirmware: Boolean(draft.gozSlave.multiMixerFirmware || next.multiMixerFirmware),
          dualReactorOffset: Boolean(draft.gozSlave.dualReactorOffset || next.dualReactorOffset),
        };
      });
    },
    setClearData: (state) => {
      // Reset the state to its initial value
      return initialState;
    },
  },
});

export const {
  setWkkFasts,
  setGozFasts,
  setGozSlaveFasts,
  setWkkSlows,
  setGozSlows,
  setGozSlaveSlows,
  setClearData,
} = machineSlice.actions;

export default machineSlice.reducer;